import React, { useRef, useState } from "react";
import { courseschedulesApi, CheckScheduleConflictsRequest, ScheduleConflictApi } from "api/courseSchedulesApi";
import { PaziaModal } from "components/molecules";
import { PaziaButton } from "components/atoms";
import { describeConflict } from "./scheduleConflicts";

/**
 * Before saving a schedule entry: asks the API whether its trainers or on-site room are already booked at an
 * overlapping time. No conflicts → resolves true at once; otherwise shows them and resolves with the user's choice.
 * A failed check never blocks saving.
 */
export const useScheduleConflictConfirm = (courseId: number) => {
  const [conflicts, setConflicts] = useState<ScheduleConflictApi[] | null>(null);
  const resolver = useRef<(ok: boolean) => void>();

  const confirmSave = async (request: CheckScheduleConflictsRequest): Promise<boolean> => {
    let found: ScheduleConflictApi[] = [];
    try {
      found = (await courseschedulesApi.checkConflicts(courseId, request)).data;
    } catch (e) {
      console.error(e);
    }
    if (found.length === 0) return true;
    setConflicts(found);
    return new Promise<boolean>((resolve) => (resolver.current = resolve));
  };

  const answer = (ok: boolean) => {
    setConflicts(null);
    resolver.current?.(ok);
  };

  const dialog = (
    <PaziaModal id="schedule-conflict-modal" isOpen={!!conflicts} onCloseRequest={() => answer(false)} size="xl">
      <div className="flex flex-col gap-4 px-5 pb-6 pt-4">
        <div className="text-xl font-medium text-slate-800 dark:text-slate-100">Kolizja w harmonogramie</div>
        <div className="text-sm text-slate-700 dark:text-slate-300">
          W tym samym czasie są już zaplanowane:
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {conflicts?.map((c, i) => (
              <li key={i}>{describeConflict(c, courseId)}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <PaziaButton title="Wróć do edycji" type="tertiary" btnType="button" onClick={() => answer(false)} />
          <PaziaButton title="Zapisz mimo to" type="yellow" btnType="button" onClick={() => answer(true)} />
        </div>
      </div>
    </PaziaModal>
  );

  return { confirmSave, dialog };
};
